// src/pages/PasswordReset.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validateResetToken } from '../services/api/AuthService';
import { resetPassword } from '../services/api/UserService';
import { usePasswordValidation } from '../hooks/usePasswordValidation';
import { VALIDATION_MESSAGES } from '../utils/passwordValidation';
import '../styles/PasswordReset.css';
import '../styles/button.css';
import '../styles/PasswordValidation.css';
import ircLogo from '../assets/img/as-finder-logo.svg';
import passwordIcon from '../assets/img/pass-word.svg';

const PasswordReset = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const passwordChangeRequired = localStorage.getItem('password_change_required');
  
  // usePasswordValidationフックを使用
  const {
    password,
    confirmPassword,
    passwordErrors,
    isPasswordMatch,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handlePasswordBlur,
    handleConfirmPasswordBlur,
    handlePasswordFocus,
    handleConfirmPasswordFocus,
    validateForm,
    showPasswordErrors,
    showPasswordMatchError,
    isPasswordSecure,
    isValidating,
    userType
  } = usePasswordValidation({
    validateOnChange: true,
    showErrorsImmediately: true,
    required: true
  });

  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [message, setMessage] = useState('');

  // トークンの有効性を検証
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError('リセットトークンがありません。有効なリンクからアクセスしてください。');
      return;
    }

    const validateToken = async () => {
      try {
        setLoading(true);
        const response = await validateResetToken(token);
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
       // ここでトークン切れエラーを判定
       const status = error.response?.status;
       if (status === 400) {
         setError('リセットトークンの有効期限が切れています。再度パスワードリセットをリクエストしてください。');
       } else {
         setError('トークンの検証中にエラーが発生しました。');
       }
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // usePasswordValidationのvalidateFormを使用
    const { isValid, errors } = validateForm();
    
    if (!isValid) {
      if (errors.password.length > 0) {
        setError(VALIDATION_MESSAGES.PASSWORD_REQUIREMENTS_NOT_MET);
      } else if (errors.confirmPassword.length > 0) {
        setError(VALIDATION_MESSAGES.PASSWORD_MISMATCH);
      }
      return;
    }
    
    try {
      setLoading(true);
      const response = await resetPassword(token, password);
      setMessage('パスワードが正常にリセットされました。');

      const userType = response.userType;

      // 3秒後に種別ごとに異なるログイン画面へリダイレクト
      setTimeout(() => {
        const notification = { notification: 'パスワードがリセットされました。新しいパスワードでログインしてください。' };
        if (userType === 'admin') {
          navigate('/admin/login', { state: notification });
        } else if (userType === 'agency') {
          navigate('/agency/login', { state: notification });
        } else {
          // solicitor などそれ以外
          navigate('/solicitor/login', { state: notification });
        }
      }, 3000);
     } catch (error) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
     } finally {
       setLoading(false);
     }
  };

  const togglePasswordVisibility1 = () => {
    setShowPassword1(!showPassword1);
  };

  const togglePasswordVisibility2 = () => {
    setShowPassword2(!showPassword2);
  };
  
  const handleBack = () => {
    navigate(-1); // 前のページに戻る
  };

  // パスワード入力変更ハンドラー
  const handlePasswordInputChange = (e) => {
    handlePasswordChange(e.target.value);
    // エラーメッセージをクリア
    if (error) {
      setError('');
    }
  };

  const handleConfirmPasswordInputChange = (e) => {
    handleConfirmPasswordChange(e.target.value);
    // エラーメッセージをクリア
    if (error) {
      setError('');
    }
  };

  const navigateToLogin = (userType, state = null) => {
    const routes = {
      admin: '/admin/login',
      agency: '/agency/login',
      solicitor: '/solicitor/login'
    };

    const targetRoute = routes[userType] || routes.solicitor;
    
    if (state) {
      navigate(targetRoute, { state });
    } else {
      navigate(targetRoute);
    }
  };

  // トークンが無効な場合の表示
  if (!tokenValid && !loading) {
    return (
      <div className="password-reset-container">
        <div className="login-background">
        </div>
        
        <div className="password-reset-card">
          <div className="logo-header">
            <img src={ircLogo} alt="IRC 株式会社アイリックコーポレーション" className="main-logo" />
          </div>
          
          <h2 className="reset-title">
            {passwordChangeRequired ? 'パスワード設定' : 'パスワードリセット'}
          </h2>
          
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="password-reset-container">
      <div className="login-background">
      </div>
      
      <div className="password-reset-card">
        <div className="logo-header">
          <img src={ircLogo} alt="IRC 株式会社アイリックコーポレーション" className="main-logo" />
        </div>
        
        <h2 className="reset-title">
          {passwordChangeRequired ? 'パスワードを設定' : 'パスワードをリセット'}
        </h2>
        
        {loading ? (
          <p className="instruction-text">読み込み中...</p>
        ) : message ? (
          <div className="success-message">
            <p>{message}</p>
            <p>ログインページに移動します...</p>
          </div>
        ) : (
          <>
            <p className="instruction-text">
              <strong>
              {passwordChangeRequired 
                ? '現在、仮パスワードでログインしています。安全にご利用いただくため、パスワードを変更してください。'
                : '新しいパスワードを入力してください。'
              }
              </strong>
            </p>
            <p className="password-requirement">
              パスワードは8文字以上で、大文字、小文字、数字、特殊文字のうち3種類以上を含む必要があります。
            </p>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handlePasswordSubmit} className="reset-form">
              <div className="input-group">
                <div className="icon-container">
                  <img src={passwordIcon} alt="パスワード" className="icon lock-icon" />
                </div>
                <input
                  type={showPassword1 ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordInputChange}
                  onBlur={handlePasswordBlur}
                  onFocus={handlePasswordFocus}
                  placeholder="パスワード"
                  className={`${showPasswordErrors ? 'error' : ''} ${isPasswordSecure ? 'success' : ''}`}
                  required
                  disabled={loading || message || isValidating}
                />
                <div className="password-toggle" onClick={togglePasswordVisibility1}>
                  <svg className="eye-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d={showPassword1 
                      ? "M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" 
                      : "M12 6c3.79 0 7.17 2.13 8.82 5.5-.59 1.22-1.42 2.27-2.41 3.12l1.41 1.41c1.39-1.23 2.49-2.77 3.18-4.53-1.73-4.39-6-7.5-11-7.5-1.27 0-2.49.2-3.64.57l1.65 1.65C10.66 6.09 11.32 6 12 6zm-1.07 1.14L13 9.21c.57.25 1.03.71 1.28 1.28l2.07 2.07c.08-.34.14-.7.14-1.07C16.5 9.01 14.48 7 12 7c-.37 0-.72.05-1.07.14zM2.01 3.87l2.68 2.68C3.06 7.83 1.77 9.53 1 11.5 2.73 15.89 7 19 12 19c1.52 0 2.98-.29 4.32-.82l3.42 3.42 1.41-1.41L3.42 2.45 2.01 3.87zm7.5 7.5l2.61 2.61c-.04.01-.08.02-.12.02-1.38 0-2.5-1.12-2.5-2.5 0-.05.01-.08.01-.13zm-3.4-3.4l1.75 1.75c-.23.55-.36 1.15-.36 1.78 0 2.48 2.02 4.5 4.5 4.5.63 0 1.23-.13 1.77-.36l.98.98c-.88.24-1.8.38-2.75.38-3.79 0-7.17-2.13-8.82-5.5.7-1.43 1.72-2.61 2.93-3.53z"} 
                      fill="#999"/>
                  </svg>
                </div>
              </div>
              
              {/* パスワードエラー表示 */}
              {showPasswordErrors && (
                <div className="password-validation-errors">
                  {passwordErrors.map((error, index) => (
                    <div key={index} className="validation-error">
                      {error}
                    </div>
                  ))}
                </div>
              )}
              
              <p className="confirmation-text">※確認のため、再入力ください</p>
              
              <div className="input-group">
                <div className="icon-container">
                  <img src={passwordIcon} alt="パスワード" className="icon lock-icon" />
                </div>
                <input
                  type={showPassword2 ? "text" : "password"}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordInputChange}
                  onBlur={handleConfirmPasswordBlur}
                  onFocus={handleConfirmPasswordFocus}
                  placeholder="パスワード"
                  className={`${showPasswordMatchError ? 'error' : ''} ${isPasswordMatch && confirmPassword ? 'success' : ''}`}
                  required
                  disabled={loading || message || isValidating}
                />
                <div className="password-toggle" onClick={togglePasswordVisibility2}>
                  <svg className="eye-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d={showPassword2 
                      ? "M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" 
                      : "M12 6c3.79 0 7.17 2.13 8.82 5.5-.59 1.22-1.42 2.27-2.41 3.12l1.41 1.41c1.39-1.23 2.49-2.77 3.18-4.53-1.73-4.39-6-7.5-11-7.5-1.27 0-2.49.2-3.64.57l1.65 1.65C10.66 6.09 11.32 6 12 6zm-1.07 1.14L13 9.21c.57.25 1.03.71 1.28 1.28l2.07 2.07c.08-.34.14-.7.14-1.07C16.5 9.01 14.48 7 12 7c-.37 0-.72.05-1.07.14zM2.01 3.87l2.68 2.68C3.06 7.83 1.77 9.53 1 11.5 2.73 15.89 7 19 12 19c1.52 0 2.98-.29 4.32-.82l3.42 3.42 1.41-1.41L3.42 2.45 2.01 3.87zm7.5 7.5l2.61 2.61c-.04.01-.08.02-.12.02-1.38 0-2.5-1.12-2.5-2.5 0-.05.01-.08.01-.13zm-3.4-3.4l1.75 1.75c-.23.55-.36 1.15-.36 1.78 0 2.48 2.02 4.5 4.5 4.5.63 0 1.23-.13 1.77-.36l.98.98c-.88.24-1.8.38-2.75.38-3.79 0-7.17-2.13-8.82-5.5.7-1.43 1.72-2.61 2.93-3.53z"} 
                      fill="#999"/>
                  </svg>
                </div>
              </div>

              {/* 確認パスワードエラー表示 */}
              {showPasswordMatchError && (
                <div className="validation-error">
                  {VALIDATION_MESSAGES.PASSWORD_MISMATCH}
                </div>
              )}
              
              <div className="button-group button-group--mt-l">
                <button 
                  type="button" 
                  className="button button-secondary button-large" 
                  onClick={handleBack}
                  disabled={loading || message}
                >
                  戻る
                </button>
                <button 
                  type="submit" 
                  className="button button-primary button-large"
                  disabled={loading || message || !password || !confirmPassword || isValidating}
                >
                  {loading ? '処理中...' : isValidating ? 'チェック中...' : '送信'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default PasswordReset;